/* eslint-disable tailwindcss/no-custom-classname */
import { api } from "@utils/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";
import {
	Card,
	Collapse,
	Flex,
	Grid,
	Group,
	Image,
	Stack,
	Text,
} from "@mantine/core";
import { StringUtils } from "@/apps/liff/pages/logistics-page/components/StringUtils";
import { CaretDown, CaretUp } from "@phosphor-icons/react";
import { AssetUtils } from "@/apps/liff/pages/logistics-page/components/AssetUtils";
import "./LogisticsDetail.scss";

export default function LogisticsPage() {
	const navigate = useNavigate();
	const [params] = useSearchParams();
	const logisticsNo = params.get("logisticsNo");
	const [isLoading, setIsLoading] = useState(true);
	const [openCard, setOpenCard] = useState({});
	const [record, setRecord] = useState(null);
	const toggleCard = (doc_no) => {
		// 更新特定卡片的展開狀態
		setOpenCard((prev) => ({
			...prev,
			[doc_no]: !prev[doc_no], // 切換該卡片的展開狀態
		}));
	};

	const logisticsData = async () => {
		if (!logisticsNo) {
			navigate("/");
		}
		try {
			await api.get(`/logistics/${logisticsNo}`).then((data) => {
				if (data.data.error !== undefined) {
					navigate("/");
					return;
				}
				setRecord(data.data);
				setIsLoading(false);
			});
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		if (record) return;
	}, [record]);

	useEffect(() => {
		logisticsData();
	}, []);

	return (
		<div className="logistics-page">
			{record && (
				<Card
					bg="white"
					py="md"
					px="lg"
					shadow="sm"
					radius="md"
					withBorder
					className="logistics-product-info"
				>
					<Flex mb="xs" direction="row" gap="5px">
						<Image
							src={window.assetUrl("/images/liff/brand-logo.png")}
							h="16px"
							w="16px"
							mt="5px"
						/>
						<Text fw="bold">訂單資訊</Text>
					</Flex>
					<Flex direction="column" gap="sm">
						<Grid className="content">
							<Grid.Col span={3}>消費日期</Grid.Col>
							<Grid.Col className="value" span={9}>
								{record.spending_date}
							</Grid.Col>
							<Grid.Col span={3}>銷售專員</Grid.Col>
							<Grid.Col className="value" span={9}>
								{record.salesperson}
							</Grid.Col>
							<Grid.Col span={3}>店舖名稱</Grid.Col>
							<Grid.Col className="value" span={9}>
								{record.store_name}
							</Grid.Col>
							<Grid.Col span={3}>訂單編號</Grid.Col>
							<Grid.Col className="value" span={9}>
								{record.doc_no}
							</Grid.Col>
							<Grid.Col span={3}>淨額</Grid.Col>
							<Grid.Col className="value" span={9}>
								{StringUtils.ntd(record.net_amt)}
							</Grid.Col>
							<Grid.Col span={3}>優惠</Grid.Col>
							<Grid.Col className="value" span={9}>
								{record.promotions
									.map((promo) => `${promo.prom_id}`)
									.join(", ")}
							</Grid.Col>
							<Grid.Col span={3}>物流單號</Grid.Col>
							<Grid.Col className="value" span={9}>
								<span className="logisticsStr">
									{record.logistics.logisticsNo}
								</span>
							</Grid.Col>
						</Grid>
						<Collapse in={openCard[record.doc_no]} transitionDuration={500}>
							<Stack gap="0.2rem">
								{record.items.map((item) => (
									<Fragment key={item.barcode}>
										<Group gap="20px" py="sm" align="start">
											<Image
												src={AssetUtils.ttrMerchandiseImage(item.style)}
												w="30%"
												mt="5px"
											/>
											<Stack gap="5px" w="60%">
												<Group className="resordContent" align="start">
													<Text className="recordTitle">商品名稱</Text>
													<Text className="contentItemName">
														{item.item_name}
													</Text>
												</Group>
												<Group className="resordContent">
													<Text className="recordTitle">商品貨號</Text>
													<Text className="contentColor">{item.style}</Text>
												</Group>
												<Group className="resordContent">
													<Text className="recordTitle">商品條碼</Text>
													<Text className="contentColor">{item.barcode}</Text>
												</Group>
												<Group className="resordContent">
													<Text className="recordTitle">商品尺寸</Text>
													<Text className="contentColor">{item.size}</Text>
												</Group>
												<Group className="resordContent">
													<Text className="recordTitle">商品數量</Text>
													<Text className="contentColor">{item.quantity}</Text>
												</Group>
												<Group className="resordContent">
													<Text className="recordTitle">商品價格</Text>
													<Text className="contentColor">
														{StringUtils.ntd(item.price)}
													</Text>
												</Group>
												<Group className="resordContent">
													<Text className="recordTitle">銷售總額</Text>
													<Text className="contentColor">
														{StringUtils.ntd(item.gross_amt)}
													</Text>
												</Group>
												<Group className="resordContent">
													<Text className="recordTitle">銷售淨額</Text>
													<Text className="contentColor">
														{StringUtils.ntd(item.net_amt)}
													</Text>
												</Group>
											</Stack>
										</Group>
									</Fragment>
								))}
							</Stack>
						</Collapse>

						<Group
							justify="center"
							py="sm"
							style={{ borderTop: "1px solid #CDCDCD" }}
							gap="5px"
						>
							<Text>{record.items.length} 項商品</Text>
							<button
								onClick={() => toggleCard(record.doc_no)}
								style={{ background: "none", border: "none" }}
							>
								{openCard[record.doc_no] ? (
									<CaretUp size={24} weight="bold" />
								) : (
									<CaretDown size={24} weight="bold" />
								)}
							</button>
						</Group>
					</Flex>
				</Card>
			)}
			{record && (
				<Card
					bg="white"
					py="md"
					px="lg"
					shadow="sm"
					radius="md"
					withBorder
					className="logistics-product-info"
				>
					<Flex mb="xs" direction="row" gap="5px">
						<Image
							src={window.assetUrl("/images/liff/brand-logo.png")}
							h="16px"
							w="16px"
							mt="5px"
						/>
						<Text fw="bold">配送狀態</Text>
					</Flex>
					<Flex direction="column" gap="sm">
						<ul className="timeline">
							{record.logistics.router?.map((item, index) => (
								<>
									<li className={index === 0 ? "active link-active" : "active"}>
										<span className="point"></span>
										<div>{item.trackOutRemark}</div>
										<div>{item.localTm}</div>
									</li>
								</>
							))}
						</ul>
					</Flex>
				</Card>
			)}

			{isLoading ? (
				<div className="pointer-events-none fixed left-0 top-0 z-[1000] flex h-svh w-full items-center justify-center bg-white">
					<img
						alt={"reload"}
						className="h-[6rem] w-[6rem] rounded-md bg-white p-4 opacity-100"
						src={window.assetUrl("/images/liff/loading.gif")}
					/>
				</div>
			) : null}

		</div>
	);
}
